/**
 * Per-object shader data (asset contract §8.2 note): materials are shared per slot × faction, and everything that
 * differs per ship reaches the shader through per-object uniforms (`onObjectUpdate`) that read the mesh's
 * `userData`: the static socket data baked here at applyShipLook/applyStaticLook (engine nozzles for soot, heat and
 * throat glow, light lenses, the pilot eye, a wear seed and the panel pitch) and the live ShipLookState (thrust,
 * damage, the last 8 hull hits). One pipeline per slot × faction, never one per ship.
 * The update callbacks allocate nothing (D-014).
 */
import { Box3, Color, Matrix4, Vector3, Vector4, type Camera, type DirectionalLight, type Object3D, type Scene, type UniformNode } from 'three/webgpu'
import { uniform } from 'three/tsl'
import type { ModelInstance } from '../../../assets/loader'
import { panelClassFor, type MaterialsFile } from '../data'
import { LOOK_STATE_KEY, MAX_HULL_HITS, type ShipLookState } from '../lookState'

export const HULL_DATA_KEY = 'eclHull'
export const MAX_ENGINES = 8
export const MAX_LIGHTS = 8
export const ENGINE_KIND = { none: 0, main: 1, retro: 2, rcs: 3 } as const

/** Far-away sentinel for unused slots (distance tests fail, no branch needed). */
export const FAR = 1e6

export interface HullObjectData {
  /** MAX_ENGINES × 8: x, y, z, nozzle radius, exhaust dir x, y, z, ENGINE_KIND (mesh-local metres). */
  readonly engines: Float32Array
  /** MAX_LIGHTS × 4: x, y, z, packed linear colour (r·65536 + g·256 + b, 8-bit each); negative = blinking; 0 = none. */
  readonly lights: Float32Array
  /** EYE_PILOT position and 1, or FAR and 0. */
  readonly eye: Float32Array
  /** seed 0..1, primary panel pitch, secondary pitch, seam width (metres). */
  readonly params: Float32Array
  /** Asset space → mesh-local space, or null when the mesh sits at the asset origin (the normal case). */
  readonly localFromAsset: Matrix4 | null
}

export function engineKindOf(name: string): number {
  if (name.startsWith('ENG_MAIN_')) return ENGINE_KIND.main
  if (name.startsWith('ENG_RETRO_')) return ENGINE_KIND.retro
  if (name.startsWith('ENG_RCS_')) return ENGINE_KIND.rcs
  return ENGINE_KIND.none
}

/** Packs a colour's linear channels into one float that survives float32 exactly (24 bits). */
export function packLinearColor(hex: string): number {
  const c = new Color(hex)
  const q = (v: number): number => Math.round(Math.min(Math.max(v, 0), 1) * 255)
  return q(c.r) * 65536 + q(c.g) * 256 + q(c.b)
}

export function unpackLinearColor(packed: number): [number, number, number] {
  const v = Math.abs(packed)
  return [Math.floor(v / 65536) / 255, (Math.floor(v / 256) % 256) / 255, (v % 256) / 255]
}

let seedCounter = 0

/** Deterministic per-instance seed in 0..1 (golden-ratio sequence in creation order). */
function nextSeed(): number {
  seedCounter++
  return (seedCounter * 0.6180339887498949) % 1
}

export function resetSeedsForTests(): void {
  seedCounter = 0
}

interface SocketPose {
  name: string
  pos: Vector3
  dir: Vector3
  params: Record<string, unknown>
}

function socketPoses(instance: ModelInstance): SocketPose[] {
  instance.root.updateMatrixWorld(true)
  const assetFromWorld = new Matrix4().copy(instance.root.matrixWorld).invert()
  const out: SocketPose[] = []
  for (const [name, node] of instance.sockets) {
    const m = new Matrix4().multiplyMatrices(assetFromWorld, node.matrixWorld)
    const pos = new Vector3().setFromMatrixPosition(m)
    const dir = new Vector3(0, 0, -1).transformDirection(m)
    out.push({ name, pos, dir, params: node.userData as Record<string, unknown> })
  }
  return out
}

/** Builds the instance's per-object data in asset space (pure apart from the seed counter). */
export function buildHullObjectData(instance: ModelInstance, data: MaterialsFile, isStatic = false): HullObjectData {
  const poses = socketPoses(instance)
  const engines = new Float32Array(MAX_ENGINES * 8)
  const lights = new Float32Array(MAX_LIGHTS * 4)
  for (let i = 0; i < MAX_ENGINES; i++) engines.set([FAR, FAR, FAR, 0, 0, 0, 1, ENGINE_KIND.none], i * 8)
  for (let i = 0; i < MAX_LIGHTS; i++) lights.set([FAR, FAR, FAR, 0], i * 4)
  // Main nozzles first (they carry the most soot and heat), then retro, then RCS; extra sockets are dropped.
  const eng = poses.filter((s) => engineKindOf(s.name) !== ENGINE_KIND.none).sort((a, b) => engineKindOf(a.name) - engineKindOf(b.name) || a.name.localeCompare(b.name))
  for (let i = 0; i < Math.min(eng.length, MAX_ENGINES); i++) {
    const s = eng[i]
    const r = typeof s.params.radius === 'number' ? s.params.radius : 0.4
    engines.set([s.pos.x, s.pos.y, s.pos.z, r, s.dir.x, s.dir.y, s.dir.z, engineKindOf(s.name)], i * 8)
  }
  const lit = poses.filter((s) => s.name.startsWith('LIGHT_')).sort((a, b) => a.name.localeCompare(b.name))
  for (let i = 0; i < Math.min(lit.length, MAX_LIGHTS); i++) {
    const s = lit[i]
    const hex = typeof s.params.color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(s.params.color) ? s.params.color : '#FFFFFF'
    const blinking = (typeof s.params.blinkHz === 'number' && s.params.blinkHz > 0) || s.params.type === 'strobe'
    const packed = Math.max(1, packLinearColor(hex))
    lights.set([s.pos.x, s.pos.y, s.pos.z, blinking ? -packed : packed], i * 4)
  }
  const eyePose = poses.find((s) => s.name === 'EYE_PILOT')
  const eye = new Float32Array(eyePose ? [eyePose.pos.x, eyePose.pos.y, eyePose.pos.z, 1] : [FAR, FAR, FAR, 0])
  const box = new Box3().setFromObject(instance.root, true)
  const radius = box.isEmpty() ? 1 : box.getSize(new Vector3()).length() / 2
  const panel = panelClassFor(data, radius, isStatic)
  const params = new Float32Array([nextSeed(), panel.primary, panel.secondary, panel.seamWidth])
  return { engines, lights, eye, params, localFromAsset: null }
}

/** The same data in a mesh's local space when the mesh does not sit at the asset origin. */
export function dataForMesh(base: HullObjectData, localFromAsset: Matrix4): HullObjectData {
  const engines = base.engines.slice()
  const lights = base.lights.slice()
  const eye = base.eye.slice()
  const v = new Vector3()
  for (let i = 0; i < MAX_ENGINES; i++) {
    const o = i * 8
    if (engines[o + 7] === ENGINE_KIND.none) continue
    v.set(engines[o], engines[o + 1], engines[o + 2]).applyMatrix4(localFromAsset)
    engines.set([v.x, v.y, v.z], o)
    v.set(engines[o + 4], engines[o + 5], engines[o + 6]).transformDirection(localFromAsset)
    engines.set([v.x, v.y, v.z], o + 4)
  }
  for (let i = 0; i < MAX_LIGHTS; i++) {
    const o = i * 4
    if (lights[o + 3] === 0) continue
    v.set(lights[o], lights[o + 1], lights[o + 2]).applyMatrix4(localFromAsset)
    lights.set([v.x, v.y, v.z], o)
  }
  if (eye[3] > 0) {
    v.set(eye[0], eye[1], eye[2]).applyMatrix4(localFromAsset)
    eye.set([v.x, v.y, v.z])
  }
  return { engines, lights, eye, params: base.params, localFromAsset }
}

/** E2 drive band (style guide §3.6): Y = idle + (full − idle) × thrust, × boost multiplier. */
export function engineY(data: MaterialsFile, thrust: number, boost: number): number {
  const e = data.emissive
  const t = Math.min(Math.max(thrust, 0), 1)
  return (e.engineIdleY + (e.engineFullY - e.engineIdleY) * t) * (boost > 0 ? e.boostMult : 1)
}

/** Retro rings light only while braking or reversing, on the same E2 law. */
export function retroY(data: MaterialsFile, retro: number): number {
  return retro > 0.01 ? engineY(data, retro, 0) : 0
}

export type Vec4Uniform = UniformNode<'vec4', Vector4>

export interface ObjectUniforms {
  /** (engine throat Y, retro throat Y, damage 0..1, boost 0/1). */
  readonly ship: Vec4Uniform
  /** MAX_HULL_HITS × (x, y, z mesh-local, t on the library clock). */
  readonly hits: readonly Vec4Uniform[]
  readonly engPos: readonly Vec4Uniform[]
  readonly engDir: readonly Vec4Uniform[]
  readonly lights: readonly Vec4Uniform[]
  readonly eye: Vec4Uniform
  readonly params: Vec4Uniform
  /** G5: (engine slots, hit slots, light slots, 0) in use; the shader loops stop there (`writeSlotCounts`). */
  readonly counts: Vec4Uniform
}

function dataOf(o: Object3D | null): HullObjectData | undefined {
  return o ? (o.userData[HULL_DATA_KEY] as HullObjectData | undefined) : undefined
}

function stateOf(o: Object3D | null): ShipLookState | undefined {
  return o ? (o.userData[LOOK_STATE_KEY] as ShipLookState | undefined) : undefined
}

const scratch = new Vector3()

/** Writes hit `i` of the look state into `out` (mesh-local), or the far sentinel. Allocation-free. */
export function writeHit(state: ShipLookState | undefined, d: HullObjectData | undefined, i: number, out: Vector4): void {
  if (!state) {
    out.set(FAR, FAR, FAR, -FAR)
    return
  }
  const r = state.hullHits
  const o = i * 4
  if (d && d.localFromAsset) {
    scratch.set(r[o], r[o + 1], r[o + 2]).applyMatrix4(d.localFromAsset)
    out.set(scratch.x, scratch.y, scratch.z, r[o + 3])
  } else out.set(r[o], r[o + 1], r[o + 2], r[o + 3])
}

/** Writes the ship vector (engine Y, retro Y, damage, boost) for an object. Allocation-free. */
export function writeShip(data: MaterialsFile, state: ShipLookState | undefined, out: Vector4): void {
  if (!state) {
    out.set(engineY(data, 0, 0), 0, 0, 0)
    return
  }
  out.set(engineY(data, state.thrust, state.boost), retroY(data, state.retro), state.damage, state.boost)
}

/** A hull hit slot never written (lookState fills hullHits with this). */
export const HIT_EMPTY = -1e6

/**
 * G5: writes (engine, hit, light slots in use, 0) for an object: one past the last slot that is not parked. Slots
 * from there on are parked (engine kind none, light colour 0, hit never written) and add exactly 0 to the shader's
 * max/select chains, so the bounded loops match the full ones. Allocation-free.
 */
export function writeSlotCounts(d: HullObjectData | undefined, state: ShipLookState | undefined, out: Vector4): void {
  let eng = 0
  let lit = 0
  let hit = 0
  if (d) {
    for (let i = 0; i < MAX_ENGINES; i++) if (d.engines[i * 8 + 7] !== ENGINE_KIND.none) eng = i + 1
    for (let i = 0; i < MAX_LIGHTS; i++) if (d.lights[i * 4 + 3] !== 0) lit = i + 1
  }
  if (state) {
    const r = state.hullHits
    for (let i = 0; i < MAX_HULL_HITS; i++) if (r[i * 4 + 3] !== HIT_EMPTY) hit = i + 1
  }
  out.set(eng, hit, lit, 0)
}

function vec4(): Vec4Uniform {
  return uniform(new Vector4())
}

/** The per-object uniform set, created once per library and shared by every slot material. */
export function createObjectUniforms(data: MaterialsFile): ObjectUniforms {
  const ship = vec4().onObjectUpdate((frame, self) => {
    writeShip(data, stateOf(frame.object), self.value)
    return undefined
  })
  const hits: Vec4Uniform[] = []
  for (let i = 0; i < MAX_HULL_HITS; i++) {
    hits.push(
      vec4().onObjectUpdate((frame, self) => {
        writeHit(stateOf(frame.object), dataOf(frame.object), i, self.value)
        return undefined
      }),
    )
  }
  const engPos: Vec4Uniform[] = []
  const engDir: Vec4Uniform[] = []
  for (let i = 0; i < MAX_ENGINES; i++) {
    engPos.push(
      vec4().onObjectUpdate((frame, self) => {
        const d = dataOf(frame.object)
        if (d) self.value.fromArray(d.engines, i * 8)
        else self.value.set(FAR, FAR, FAR, 0)
        return undefined
      }),
    )
    engDir.push(
      vec4().onObjectUpdate((frame, self) => {
        const d = dataOf(frame.object)
        if (d) self.value.fromArray(d.engines, i * 8 + 4)
        else self.value.set(0, 0, 1, ENGINE_KIND.none)
        return undefined
      }),
    )
  }
  const lights: Vec4Uniform[] = []
  for (let i = 0; i < MAX_LIGHTS; i++) {
    lights.push(
      vec4().onObjectUpdate((frame, self) => {
        const d = dataOf(frame.object)
        if (d) self.value.fromArray(d.lights, i * 4)
        else self.value.set(FAR, FAR, FAR, 0)
        return undefined
      }),
    )
  }
  const eye = vec4().onObjectUpdate((frame, self) => {
    const d = dataOf(frame.object)
    if (d) self.value.fromArray(d.eye)
    else self.value.set(FAR, FAR, FAR, 0)
    return undefined
  })
  const params = vec4().onObjectUpdate((frame, self) => {
    const d = dataOf(frame.object)
    if (d) self.value.fromArray(d.params)
    else self.value.set(0, 1.6, 0.45, 0.015)
    return undefined
  })
  // Named so the WGSL reads `object.slotCounts` (the G5 branch test finds the guarded slot blocks by it).
  const counts = vec4().setName('slotCounts').onObjectUpdate((frame, self) => {
    writeSlotCounts(dataOf(frame.object), stateOf(frame.object), self.value)
    return undefined
  })
  return { ship, hits, engPos, engDir, lights, eye, params, counts }
}

// ---------------------------------------------------------------- key light (rim mask, style guide §3.4)

let sunScene: Scene | null = null
let sun: DirectionalLight | null = null
const sunPos = new Vector3()
const sunTarget = new Vector3()

/** The environment's key light (named `sun`, environment.ts), looked up once per scene. */
function findSun(scene: Scene | null): DirectionalLight | null {
  if (!scene) return null
  if (scene !== sunScene || (sun !== null && sun.parent === null)) {
    sunScene = scene
    const found = scene.getObjectByName('sun') as DirectionalLight | undefined
    sun = found && found.isDirectionalLight ? found : null
  }
  return sun
}

/** Writes (key direction in view space, key on 0/1) for the current render. Allocation-free after the first lookup. */
export function writeKey(scene: Scene | null, camera: Camera | null, out: Vector4): void {
  const light = findSun(scene)
  if (!light || !camera || light.intensity <= 0 || !light.visible) {
    out.set(0, 0, 1, 0)
    return
  }
  sunPos.setFromMatrixPosition(light.matrixWorld)
  sunTarget.setFromMatrixPosition(light.target.matrixWorld)
  sunPos.sub(sunTarget)
  if (sunPos.lengthSq() < 1e-12) sunPos.set(0, 1, 0)
  sunPos.normalize().transformDirection(camera.matrixWorldInverse)
  out.set(sunPos.x, sunPos.y, sunPos.z, 1)
}

export function createKeyUniform(): Vec4Uniform {
  return vec4().onRenderUpdate((frame, self) => {
    writeKey(frame.scene, frame.camera, self.value)
    return undefined
  })
}
